/**
 * Utility functions for combining multiple data source series into a single chart
 */

/**
 * Aligns table data to primary categories using closest timestamp matching
 * @param {string[]} tableCategories - Categories from table data source
 * @param {any[]} tableData - Data values from table data source
 * @param {string[]} primaryCategories - Primary categories to align to
 * @returns {any[]} Aligned data array with nulls where no match
 */
export const alignToCategories = (tableCategories, tableData, primaryCategories) => {
  if (!primaryCategories?.length || !tableCategories?.length) return tableData;
  
  // Create aligned data array with nulls
  const aligned = new Array(primaryCategories.length).fill(null);
  
  // For each table data point, find closest primary category
  tableCategories.forEach((tableCat, idx) => {
    // Find exact match first
    let matchIdx = primaryCategories.indexOf(tableCat);
    if (matchIdx === -1) {
      // No exact match - find closest timestamp by parsing dates
      const tableDate = new Date(tableCat);
      if (!Number.isNaN(tableDate.getTime())) {
        let minDiff = Infinity;
        primaryCategories.forEach((primCat, primIdx) => {
          const primDate = new Date(primCat);
          const diff = Math.abs(primDate.getTime() - tableDate.getTime());
          if (diff < minDiff) {
            minDiff = diff;
            matchIdx = primIdx;
          }
        });
      }
    }
    if (matchIdx !== -1) {
      aligned[matchIdx] = tableData[idx];
    }
  });
  
  return aligned;
};

/**
 * Combines series data from multiple sources with proper Y-axis configuration
 * @param {Object[]} allSeriesData - Array of series with data, categories, yAxisIndex, isMetricValues flags
 * @param {Object} options - Configuration options
 * @param {Object} options.metricValuesSource - The metric_values data source config (if any)
 * @param {Object[]} options.tableSources - Array of table data source configs
 * @returns {Object} Combined data with categories, series, dualYAxis, and yAxisConfig
 */
export const combineSeriesData = (allSeriesData, options = {}) => {
  const { metricValuesSource, tableSources = [] } = options;
  
  if (!allSeriesData?.length) {
    return null;
  }
  
  // Prioritize metric_values categories (finer granularity)
  const metricValuesSeries = allSeriesData.find(s => s.isMetricValues && s.categories);
  const primaryCategories = metricValuesSeries?.categories 
    || allSeriesData.find(s => s.categories)?.categories 
    || [];
  
  // Align all series to primary categories
  const series = allSeriesData.map((s) => {
    // If this series has its own categories and they differ from primary, align them
    if (s.categories && s.categories !== primaryCategories && !s.isMetricValues) {
      return {
        name: s.name,
        data: alignToCategories(s.categories, s.data, primaryCategories),
        yAxisIndex: s.yAxisIndex,
      };
    }
    return {
      name: s.name,
      data: s.data,
      yAxisIndex: s.yAxisIndex,
    };
  });
  
  // Enable dual Y-axis when there are multiple data sources
  const hasDualSources = tableSources.length > 1 || (metricValuesSource && tableSources.length > 0);
  
  // Build Y-axis config with proper labels
  const yAxisConfig = {};
  if (hasDualSources) {
    // Left axis: metric_values (if exists) or first table source
    if (metricValuesSource) {
      const aggFunc = metricValuesSource.config?.aggregateFunction || 'mean';
      const aggLiteral = metricValuesSource.config?.aggregateLiteral || '5m';
      yAxisConfig.left = { name: `${aggFunc} (${aggLiteral})` };
    } else if (tableSources[0]) {
      const yField = tableSources[0].config?.yAxisField || '';
      yAxisConfig.left = { name: yField.split('.').pop() || tableSources[0].id };
    }
    // Right axis: first table source (if metric_values exists) or second table source
    const rightSource = metricValuesSource ? tableSources[0] : tableSources[1];
    if (rightSource) {
      const yField = rightSource.config?.yAxisField || '';
      yAxisConfig.right = { name: yField.split('.').pop() || rightSource.id };
    }
  }
  
  return {
    categories: primaryCategories,
    series,
    dualYAxis: hasDualSources,
    yAxisConfig: hasDualSources ? yAxisConfig : undefined,
  };
};

/**
 * Determines the Y-axis index for a table source
 * @param {boolean} hasMetricValues - Whether metric_values source exists
 * @param {number} sourceIndex - Index of this table source
 * @returns {number} Y-axis index (0 for left, 1 for right)
 */
export const getTableYAxisIndex = (hasMetricValues, sourceIndex) => {
  if (hasMetricValues) {
    return 1; // All tables go to right axis when metric_values exists
  }
  return sourceIndex > 0 ? 1 : 0; // First table=0, subsequent=1
};
